////
/// ============================================================================
/// COMPONENT / Slideshow
/// ============================================================================
///
/// @link https://codyhouse.co/ds/components/app/slideshow
/// @group components
////

import SwipeContent from "./swipe-content";

function cssSupports(property, value) {
    if ('CSS' in window) {
        return CSS.supports(property, value);
    } else {
        var jsProperty = property.replace(/-([a-z])/g, function (g) {
            return g[1].toUpperCase();
        });
        return jsProperty in document.body.style;
    }
}

// Move focus to an element
function moveFocus(element) {
    if (!element) element = document.getElementsByTagName("body")[0];
    element.focus();
    if (document.activeElement !== element) {
        element.setAttribute('tabindex', '-1');
        element.focus();
    }
}

function getIndexInArray(array, el) {
    return Array.prototype.indexOf.call(array, el);
}

function setAttributes(el, attrs) {
    for (var key in attrs) {
        el.setAttribute(key, attrs[key]);
    }
}

var Slideshow = function (opts) {
    this.options = slideshowAssignOptions(Slideshow.defaults, opts);
    this.element = this.options.element;
    this.items = this.element.getElementsByClassName('js-slideshow-item');
    this.controls = this.element.getElementsByClassName('js-slideshow-control');
    this.selectedSlide = 0;
    this.autoplayId = false;
    this.autoplayPaused = false;
    this.navigation = false;
    this.navCurrentLabel = false;
    this.ariaLive = false;
    this.moveFocus = false;
    this.animating = false;
    this.supportAnimation = cssSupports('transition');
    this.animationOff = (!this.element.classList.contains('-transition-fade') && !this.element.classList.contains('-transition-slide') && !this.element.classList.contains('-transition-prx'));
    this.animationType = this.element.classList.contains('-transition-prx') ? 'prx' : 'slide';
    this.animatingClass = 'is-animating';
    initSlideshow(this);
    initSlideshowEvents(this);
    initAnimationEndEvents(this);
};

Slideshow.prototype.showNext = function () {
    showNewItem(this, this.selectedSlide + 1, 'next');
};

Slideshow.prototype.showPrev = function () {
    showNewItem(this, this.selectedSlide - 1, 'prev');
};

Slideshow.prototype.showItem = function (index) {
    showNewItem(this, index, false);
};

Slideshow.prototype.startAutoplay = function () {
    var self = this;
    if (this.options.autoplay && !this.autoplayId && !this.autoplayPaused) {
        self.autoplayId = setInterval(function () {
            self.showNext();
        }, self.options.autoplayInterval);
    }
};

Slideshow.prototype.pauseAutoplay = function () {
    var self = this;
    if (this.options.autoplay) {
        clearInterval(self.autoplayId);
        self.autoplayId = false;
    }
};

function slideshowAssignOptions(defaults, opts) {
    // initialize the object options
    var mergeOpts = {};
    mergeOpts.element = (typeof opts.element !== "undefined") ? opts.element : defaults.element;
    mergeOpts.navigation = (typeof opts.navigation !== "undefined") ? opts.navigation : defaults.navigation;
    mergeOpts.autoplay = (typeof opts.autoplay !== "undefined") ? opts.autoplay : defaults.autoplay;
    mergeOpts.autoplayInterval = (typeof opts.autoplayInterval !== "undefined") ? opts.autoplayInterval : defaults.autoplayInterval;
    mergeOpts.swipe = (typeof opts.swipe !== "undefined") ? opts.swipe : defaults.swipe;
    return mergeOpts;
}

function initSlideshow(slideshow) { // basic slideshow settings
    // if no slide has been selected -> select the first one
    if (slideshow.element.getElementsByClassName('is-selected').length < 1) {
        slideshow.items[0].classList.add('is-selected');
    }
    slideshow.selectedSlide = getIndexInArray(slideshow.items, slideshow.element.getElementsByClassName('is-selected')[0]);
    // create an element that will be used to announce the new visible slide to SR
    var srLiveArea = document.createElement('div');
    setAttributes(srLiveArea, {
        'class': 'sr-only js-slideshow-aria-live',
        'aria-live': 'polite',
        'aria-atomic': 'true'
    });
    slideshow.element.appendChild(srLiveArea);
    slideshow.ariaLive = srLiveArea;
}

function initSlideshowEvents(slideshow) {
    // if slideshow navigation is on -> create navigation HTML and add event listeners
    if (slideshow.options.navigation) {
        // check if navigation has already been included
        if (slideshow.element.getElementsByClassName('js-slideshow-navigation').length === 0) {
            var navigation = document.createElement('ol'),
                navChildren = '';

            var navClasses = 'c-slideshow_navigation js-slideshow-navigation';
            if (slideshow.items.length <= 1) {
                navClasses = navClasses + ' is-hidden';
            }

            navigation.setAttribute('class', navClasses);
            for (var i = 0; i < slideshow.items.length; i++) {
                var className = (i === slideshow.selectedSlide) ? 'class="c-slideshow_nav-item is-selected js-slideshow-nav-item"' : 'class="c-slideshow_nav-item js-slideshow-nav-item"',
                    navCurrentLabel = (i === slideshow.selectedSlide) ? '<span class="sr-only js-slideshow-nav-current-label">Current Item</span>' : '';
                navChildren = navChildren + '<li ' + className + '><button class="reset"><span class="sr-only">' + (i + 1) + '</span>' + navCurrentLabel + '</button></li>';
            }
            navigation.innerHTML = navChildren;
            slideshow.element.appendChild(navigation);
        }

        slideshow.navCurrentLabel = slideshow.element.getElementsByClassName('js-slideshow-nav-current-label')[0];
        slideshow.navigation = slideshow.element.getElementsByClassName('js-slideshow-nav-item');

        var dotsNavigation = slideshow.element.getElementsByClassName('js-slideshow-navigation')[0];

        dotsNavigation.addEventListener('click', function (event) {
            navigateSlide(slideshow, event, true);
        });
        dotsNavigation.addEventListener('keyup', function (event) {
            navigateSlide(slideshow, event, (event.key.toLowerCase() === 'enter'));
        });
    }
    // slideshow arrow controls
    if (slideshow.controls.length > 0) {
        // hide controls if one item available
        if (slideshow.items.length <= 1) {
            slideshow.controls[0].classList.add('is-hidden');
            slideshow.controls[1].classList.add('is-hidden');
        }
        slideshow.controls[0].addEventListener('click', function (event) {
            event.preventDefault();
            slideshow.showPrev();
            updateAriaLive(slideshow);
        });
        slideshow.controls[1].addEventListener('click', function (event) {
            event.preventDefault();
            slideshow.showNext();
            updateAriaLive(slideshow);
        });
    }
    // swipe events
    if (slideshow.options.swipe) {
        //init swipe
        new SwipeContent(slideshow.element);
        slideshow.element.addEventListener('swipeLeft', function (event) {
            slideshow.showNext();
        });
        slideshow.element.addEventListener('swipeRight', function (event) {
            slideshow.showPrev();
        });
    }
    // autoplay
    if (slideshow.options.autoplay) {
        slideshow.startAutoplay();
        // pause autoplay if user is interacting with the slideshow
        slideshow.element.addEventListener('mouseenter', function (event) {
            slideshow.pauseAutoplay();
            slideshow.autoplayPaused = true;
        });
        slideshow.element.addEventListener('focusin', function (event) {
            slideshow.pauseAutoplay();
            slideshow.autoplayPaused = true;
        });
        slideshow.element.addEventListener('mouseleave', function (event) {
            slideshow.autoplayPaused = false;
            slideshow.startAutoplay();
        });
        slideshow.element.addEventListener('focusout', function (event) {
            slideshow.autoplayPaused = false;
            slideshow.startAutoplay();
        });
    }
    // detect if external buttons control the slideshow
    var slideshowId = slideshow.element.getAttribute('id');
    if (slideshowId) {
        var externalControls = document.querySelectorAll('[data-controls="' + slideshowId + '"]');
        for (var i = 0; i < externalControls.length; i++) {
            (function (i) {
                externalControlSlide(slideshow, externalControls[i]);
            })(i);
        }
    }
    // custom event to trigger selection of a new slide element
    slideshow.element.addEventListener('selectNewItem', function (event) {
        // check if slide is already selected
        if (event.detail) {
            if (event.detail - 1 === slideshow.selectedSlide) return;
            showNewItem(slideshow, event.detail - 1, false);
        }
    });

    // keyboard navigation
    slideshow.element.addEventListener('keydown', function (event) {
        if (event.keyCode && event.keyCode === 39 || event.key && event.key.toLowerCase() === 'arrowright') {
            slideshow.showNext();
        } else if (event.keyCode && event.keyCode === 37 || event.key && event.key.toLowerCase() === 'arrowleft') {
            slideshow.showPrev();
        }
    });
}

function navigateSlide(slideshow, event, keyNav) {
    // user has interacted with the slideshow navigation -> update visible slide
    var target = (event.target.classList.contains('js-slideshow-nav-item')) ? event.target : event.target.closest('.js-slideshow-nav-item');
    if (keyNav && target && !target.classList.contains('is-selected')) {
        slideshow.showItem(getIndexInArray(slideshow.navigation, target));
        slideshow.moveFocus = true;
        updateAriaLive(slideshow);
    }
}

function initAnimationEndEvents(slideshow) {
    // remove animation classes at the end of a slide transition
    for (var i = 0; i < slideshow.items.length; i++) {
        (function (i) {
            slideshow.items[i].addEventListener('animationend', function () {
                resetAnimationEnd(slideshow, slideshow.items[i]);
            });
            slideshow.items[i].addEventListener('transitionend', function () {
                resetAnimationEnd(slideshow, slideshow.items[i]);
            });
        })(i);
    }
}

function resetAnimationEnd(slideshow, item) {
    setTimeout(function () { // add a delay between the end of animation and slideshow reset - improve animation performance
        if (item.classList.contains('is-selected')) {
            if (slideshow.moveFocus) moveFocus(item);
            emitSlideshowEvent(slideshow, 'newItemVisible', slideshow.selectedSlide);
            slideshow.moveFocus = false;
        }
        item.classList.remove('-' + slideshow.animationType + '-out-left', '-' + slideshow.animationType + '-out-right', '-' + slideshow.animationType + '-in-left', '-' + slideshow.animationType + '-in-right');
        item.removeAttribute('aria-hidden');
        slideshow.animating = false;
        slideshow.element.classList.remove(slideshow.animatingClass);
    }, 100);
}

function showNewItem(slideshow, index, bool) {
    if (slideshow.items.length <= 1) return;
    if (slideshow.animating && slideshow.supportAnimation) return;
    slideshow.animating = true;
    slideshow.element.classList.add(slideshow.animatingClass);
    if (index < 0) index = slideshow.items.length - 1;
    else if (index >= slideshow.items.length) index = 0;
    // skip slideshow item if it is hidden
    if (bool && slideshow.items[index].classList.contains('is-hidden')) {
        slideshow.animating = false;
        index = bool === 'next' ? index + 1 : index - 1;
        showNewItem(slideshow, index, bool);
        return;
    }
    // index of new slide is equal to index of slide selected item
    if (index === slideshow.selectedSlide) {
        slideshow.animating = false;
        return;
    }
    var exitItemClass = getExitItemClass(slideshow, bool, slideshow.selectedSlide, index);
    var enterItemClass = getEnterItemClass(slideshow, bool, slideshow.selectedSlide, index);
    // transition between slides
    if (!slideshow.animationOff) {
        slideshow.items[slideshow.selectedSlide].classList.add(exitItemClass);
    }
    slideshow.items[slideshow.selectedSlide].classList.remove('is-selected');
    slideshow.items[slideshow.selectedSlide].setAttribute('aria-hidden', 'true'); //hide to sr element that is exiting the viewport
    if (slideshow.animationOff) {
        slideshow.items[index].classList.add('is-selected');
    } else {
        slideshow.items[index].classList.add(enterItemClass, 'is-selected');
    }
    // reset slider navigation appearance
    resetSlideshowNav(slideshow, index, slideshow.selectedSlide);
    slideshow.selectedSlide = index;
    // reset autoplay
    slideshow.pauseAutoplay();
    slideshow.startAutoplay();
    // reset controls/navigation color themes
    resetSlideshowTheme(slideshow, index);
    // emit event
    emitSlideshowEvent(slideshow, 'newItemSelected', slideshow.selectedSlide);
    if (slideshow.animationOff) {
        slideshow.animating = false;
        slideshow.element.classList.remove(slideshow.animatingClass);
    }
}

function getExitItemClass(slideshow, bool, oldIndex, newIndex) {
    var className = '';
    if (bool) {
        className = (bool == 'next') ? '-' + slideshow.animationType + '-out-right' : '-' + slideshow.animationType + '-out-left';
    } else {
        className = (newIndex < oldIndex) ? '-' + slideshow.animationType + '-out-left' : '-' + slideshow.animationType + '-out-right';
    }
    return className;
};

function getEnterItemClass(slideshow, bool, oldIndex, newIndex) {
    var className = '';
    if (bool) {
        className = (bool == 'next') ? '-' + slideshow.animationType + '-in-right' : '-' + slideshow.animationType + '-in-left';
    } else {
        className = (newIndex < oldIndex) ? '-' + slideshow.animationType + '-in-left' : '-' + slideshow.animationType + '-in-right';
    }
    return className;
};

function resetSlideshowNav(slideshow, newIndex, oldIndex) {
    if (slideshow.navigation) {
        slideshow.navigation[oldIndex].classList.remove('is-selected');
        slideshow.navigation[newIndex].classList.add('is-selected');
        slideshow.navCurrentLabel.parentElement.removeChild(slideshow.navCurrentLabel);
        slideshow.navigation[newIndex].getElementsByTagName('button')[0].appendChild(slideshow.navCurrentLabel);
    }
}

function resetSlideshowTheme(slideshow, newIndex) {
    var dataTheme = slideshow.items[newIndex].getAttribute('data-theme');
    if (dataTheme) {
        if (slideshow.navigation) slideshow.navigation[0].parentElement.setAttribute('data-theme', dataTheme);
        if (slideshow.controls[0]) slideshow.controls[0].parentElement.setAttribute('data-theme', dataTheme);
    } else {
        if (slideshow.navigation) slideshow.navigation[0].parentElement.removeAttribute('data-theme');
        if (slideshow.controls[0]) slideshow.controls[0].parentElement.removeAttribute('data-theme');
    }
}

function emitSlideshowEvent(slideshow, eventName, detail) {
    var event = new CustomEvent(eventName, {detail: detail});
    slideshow.element.dispatchEvent(event);
}

function updateAriaLive(slideshow) {
    slideshow.ariaLive.innerHTML = 'Item ' + (slideshow.selectedSlide + 1) + ' of ' + slideshow.items.length;
}

function externalControlSlide(slideshow, button) { // control slideshow using external element
    button.addEventListener('click', function (event) {
        var index = button.getAttribute('data-index');
        if (!index || index == slideshow.selectedSlide + 1) return;
        event.preventDefault();
        showNewItem(slideshow, index - 1, false);
    });
}

Slideshow.defaults = {
    element: '',
    navigation: true,
    autoplay: false,
    autoplayInterval: 5000,
    swipe: false
};

// window.Slideshow = Slideshow;
//
// //initialize the Slideshow objects
// var slideshows = document.getElementsByClassName('js-slideshow');
// if (slideshows.length > 0) {
//     for (var i = 0; i < slideshows.length; i++) {
//         (function (i) {
//             var navigation = (slideshows[i].getAttribute('data-navigation') && slideshows[i].getAttribute('data-navigation') == 'off') ? false : true,
//                 autoplay = (slideshows[i].getAttribute('data-autoplay') && slideshows[i].getAttribute('data-autoplay') == 'on') ? true : false,
//                 autoplayInterval = (slideshows[i].getAttribute('data-autoplay-interval')) ? slideshows[i].getAttribute('data-autoplay-interval') : 5000,
//                 swipe = (slideshows[i].getAttribute('data-swipe') && slideshows[i].getAttribute('data-swipe') == 'on') ? true : false;
//             new Slideshow({
//                 element: slideshows[i],
//                 navigation: navigation,
//                 autoplay: autoplay,
//                 autoplayInterval: autoplayInterval,
//                 swipe: swipe
//             });
//         })(i);
//     }
// }

export default Slideshow;